import { useState } from 'react';
import { ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import { formatGel } from '../lib/format';
import { parseMoneyToTetri, tetriToInput } from '../lib/money';
import { useTakePayment } from '../lib/queries';
import { Alert, Amount, Button, Card, MoneyInput, SectionTitle } from './ui';

const METHODS = ['CASH', 'CARD', 'BANK_TRANSFER', 'OTHER'] as const;

const LABELS: Record<typeof METHODS[number], 'method.CASH' | 'method.CARD' | 'method.BANK_TRANSFER' | 'method.OTHER'> = {
  CASH: 'method.CASH',
  CARD: 'method.CARD',
  BANK_TRANSFER: 'method.BANK_TRANSFER',
  OTHER: 'method.OTHER',
};

/**
 * Money arriving after the order was written down.
 *
 * Shown only while something is owed. Defaults to the whole outstanding
 * amount, because that is what usually happens — the customer came back from
 * the cash machine — while still allowing part of it.
 */
export function TakePayment({
  orderId, payableTetri, paidTetri,
}: { orderId: string; payableTetri: number; paidTetri: number }) {
  const { t } = useI18n();
  const take = useTakePayment(orderId);
  const errorText = useApiError();

  const outstanding = Math.max(0, payableTetri - paidTetri);
  const [amount, setAmount] = useState(() => tetriToInput(outstanding));
  const [method, setMethod] = useState<typeof METHODS[number]>('CASH');

  if (outstanding === 0) return null;

  const entered = parseMoneyToTetri(amount);
  const valid = entered > 0 && entered <= outstanding;

  return (
    <Card>
      <SectionTitle>{t('payment.owedTitle')}</SectionTitle>

      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <span className="text-sm text-ink-dim">
          {paidTetri > 0
            ? t('payment.partlyPaid', { paid: formatGel(paidTetri), total: formatGel(payableTetri) })
            : t('payment.nothingPaid')}
        </span>
        <Amount value={formatGel(outstanding)} className="text-xl font-bold text-ink" />
      </div>

      {take.error instanceof ApiError && (
        <div className="mt-3"><Alert>{errorText(take.error) ?? take.error.message}</Alert></div>
      )}

      <div className="mt-3 grid grid-cols-2 gap-2">
        {METHODS.map((option) => (
          <button
            key={option}
            onClick={() => setMethod(option)}
            aria-pressed={method === option}
            className={`inline-flex min-h-11 items-center justify-center rounded-lg border px-2 text-xs font-semibold transition-colors ${
              method === option
                ? 'border-brand bg-brand-wash text-brand'
                : 'border-line bg-raised text-ink-dim hover:border-ink-faint hover:text-ink'
            }`}
          >
            {t(LABELS[option])}
          </button>
        ))}
      </div>

      <div className="mt-3">
        <MoneyInput
          label={t('payment.amount')}
          value={amount}
          hint={t('payment.upTo', { amount: formatGel(outstanding) })}
          onChange={(event) => setAmount(event.target.value)}
        />
      </div>

      <Button
        className="mt-3 w-full"
        disabled={!valid}
        loading={take.isPending}
        onClick={() => take.mutate({ method, amountTetri: entered }, {
          onSuccess: (result) => setAmount(tetriToInput(result.outstandingTetri)),
        })}
      >
        {t('payment.take', { amount: formatGel(valid ? entered : outstanding) })}
      </Button>
    </Card>
  );
}
